import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';

/**
 * PieChartWidget - Renders a pie chart for distribution data
 * 
 * @param {Array} data - Array of data points with xLabel and yValue
 * @param {Object} config - Widget configuration (title, description, etc.)
 */
export default function PieChartWidget({ data, config, height = 300 }) {
  // Aggregate data by label to avoid duplicates and sum values
  const aggregatedData = data.reduce((acc, item) => {
    const label = item.xLabel || 'Unknown';
    const value = parseFloat(item.yValue || 0);

    if (!acc[label]) {
      acc[label] = { label, value: 0 };
    }
    acc[label].value += value;
    return acc;
  }, {});

  // Parse metadata for donut configuration from widget definition
  let isDonut = false;
  let donutSettings = { innerRadius: 80, paddingAngle: 5, cornerRadius: 5 };

  if (config?.metadata) {
      try {
          const meta = JSON.parse(config.metadata);
          if (meta.donut) {
              isDonut = true;
              if (meta.innerRadius !== undefined) donutSettings.innerRadius = meta.innerRadius;
              if (meta.paddingAngle !== undefined) donutSettings.paddingAngle = meta.paddingAngle;
              if (meta.cornerRadius !== undefined) donutSettings.cornerRadius = meta.cornerRadius;
          }
      } catch (e) {
          // ignore
      }
  }

  // Transform data for MUI X-Charts PieChart
  const pieData = Object.values(aggregatedData).map((item, index) => ({
    id: index,
    value: item.value,
    label: item.label,
  }));

  // If only one data point (after aggregation), show as a big number like LineChart
  if (pieData.length === 1) {
    return (
      <Box 
        height={height} 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center"
      >
        <Typography variant="h2" color="primary.main" fontWeight="bold">
          {pieData[0].value.toLocaleString()}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {pieData[0].label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="flex-start">
      <PieChart
        series={[
          {
            data: pieData,
            highlightScope: { faded: 'global', highlighted: 'item' },
            faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
            innerRadius: isDonut ? donutSettings.innerRadius : 0,
            paddingAngle: isDonut ? donutSettings.paddingAngle : 0,
            cornerRadius: isDonut ? donutSettings.cornerRadius : 0,
          },
        ]}
        height={height}
        margin={{ top: 10, right: 200, bottom: 10, left: 0 }}
        slotProps={{
            legend: {
                direction: 'column',
                position: { vertical: 'middle', horizontal: 'right' },
                padding: 0,
            }
        }}
      />
    </Box>
  );
}
